/* eslint-disable @typescript-eslint/no-use-before-define */
import type {
  MutationAddBoardStarArgs,
  MutationRemoveBoardStarArgs,
  MutationAddSavedSearchArgs,
  MutationDeleteSavedSearchArgs,
  MutationUpdateUserLocaleArgs,
  MutationUpdateMarketingOptInArgs,
  MutationUpdateTeamifyVoluntaryDoneArgs,
  MutationDeleteOneTimeMessagesDismissedArgs,
  MutationEnableMemberProfileSyncArgs,
  MutationUnblockMemberProfileSyncArgs,
  MutationAcceptDeveloperTermsArgs,
  MutationAddCampaignArgs,
  MutationUpdateCampaignArgs,
  MutationChangeMemberEmailArgs,
  MutationResendVerificationEmailArgs,
  QueryMemberCardsArgs,
  QueryMemberSearchArgs,
  Member_Atlassian_Organization,
  MutationUpdateMemberActiveChannelArgs,
  MutationUpdateBoardStarArgs,
  MutationUpdateMemberProfileArgs,
  MutationAddOneTimeMessagesDismissedArgs,
  MutationAddMessageDismissedArgs,
  MutationUpdateNotificationEmailFrequencyArgs,
} from '../generated';
import Cookie from 'js-cookie';
import { prepareDataForApolloCache } from '../prepareDataForApolloCache';
import type { TrelloRestResolver } from '../types';
import { token } from '@trello/session-cookie';
import { clientVersion, atlassianApiGateway } from '@trello/config';
import { parseNetworkError } from '@trello/graphql-error-handling';
import { trelloFetch, fetch } from '@trello/fetch';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { getNetworkClient } from '../getNetworkClient';
import { featureFlagClient } from '@trello/feature-flag-client';
import { isQueryInfo } from '../isQueryInfo';
import { atlassianOrganizationSsoUrl } from '../atlassianOrganizationSsoUrl';
import { atlassianOrganizationDomains } from '../atlassianOrganizationDomains';

export const addBoardStar: TrelloRestResolver<MutationAddBoardStarArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/member/${args.memberId}/boardStars`),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
        idBoard: args.boardId,
        pos: args.pos,
      }),
    },
  );

  const member = await response.json();

  return prepareDataForApolloCache(member, rootNode);
};

export const removeBoardStar: TrelloRestResolver<MutationRemoveBoardStarArgs> = async (
  obj,
  args,
  context,
) => {
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/member/${args.memberId}/boardStars/${args.boardStarId}`,
  );
  const response = await fetch(apiUrl, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });

    throw await parseNetworkError(response);
  }

  return true;
};

export const updateBoardStar: TrelloRestResolver<MutationUpdateBoardStarArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(
      `/1/member/${args.memberId}/boardStars/${args.boardStarId}`,
    ),
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
        pos: args.pos,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }

  const json = await response.json();

  return prepareDataForApolloCache(json, rootNode);
};

export const addSavedSearch: TrelloRestResolver<MutationAddSavedSearchArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/member/${args.memberId}/savedSearches`,
  );
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      ...args.savedSearch,
      token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache(await response.json(), rootNode);
};

export const deleteSavedSearch: TrelloRestResolver<MutationDeleteSavedSearchArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/member/${args.memberId}/savedSearches/${args.savedSearchId}`,
  );
  const response = await fetch(apiUrl, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache(await response.json(), rootNode);
};

export const updateUserLocale: TrelloRestResolver<MutationUpdateUserLocaleArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const searchParams = new URLSearchParams();

  searchParams.set('prefs/locale', args.locale);
  searchParams.set('token', context.token || token || '');

  const networkClient = getNetworkClient();

  const response = await fetch(
    networkClient.getUrl(`/1/members/${args.memberId}`),
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: searchParams,
    },
  );

  const member = await response.json();

  return prepareDataForApolloCache(member, rootNode);
};

export const updateTeamifyVoluntaryDone: TrelloRestResolver<MutationUpdateTeamifyVoluntaryDoneArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const searchParams = new URLSearchParams();
  searchParams.set('token', context.token || token || '');

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/members/${args.memberId}/teamify/voluntaryDone`,
  );
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: searchParams,
  });

  if (response.ok) {
    return prepareDataForApolloCache(await response.json(), rootNode);
  }

  sendNetworkErrorEvent({
    url: apiUrl,
    response: await response.clone().text(),
    status: response.status,
    operationName: context.operationName,
  });
  throw await parseNetworkError(response);
};

export const enableMemberProfileSync: TrelloRestResolver<MutationEnableMemberProfileSyncArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const searchParams = new URLSearchParams();
  searchParams.set('token', context.token || token || '');

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/member/${args.memberId}/atlassianAccount/clearProfileSyncBlock`,
  );

  const postResponse = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: searchParams.toString(),
  });

  if (!postResponse.ok) {
    // Sometimes the server sends us just the string as the error, however
    // sometimes the server also sends a JSON error with the key 'message'
    // as the error
    let message = await postResponse.text(); // Parse it as text
    let data = null;

    try {
      data = JSON.parse(message); // Try to parse it as json
      message = data.message;
      // eslint-disable-next-line no-empty
    } catch {}

    sendNetworkErrorEvent({
      url: apiUrl,
      response: await postResponse.clone().text(),
      status: postResponse.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(postResponse);
  }

  // Re-fetch the Member so that the return will correctly update the apollo cache
  const refetchUrl = networkClient.getUrl(
    `/1/member/${args.memberId}?fields=aaBlockSyncUntil`,
  );
  const response = await trelloFetch(refetchUrl, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'enableMemberProfileSync',
      operationType: 'query',
      operationName: context.operationName,
    },
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: refetchUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const member = await response.json();
  return prepareDataForApolloCache(member, rootNode);
};

export const unblockMemberProfileSync: TrelloRestResolver<MutationUnblockMemberProfileSyncArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const searchParams = new URLSearchParams();
  searchParams.set('token', context.token || token || '');

  const networkClient = getNetworkClient();

  const postResponse = await fetch(
    networkClient.getUrl(
      `/1/member/${args.memberId}/atlassianAccount/migrationComplete`,
    ),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: searchParams.toString(),
    },
  );

  if (!postResponse.ok) {
    // Sometimes the server sends us just the string as the error, however
    // sometimes the server also sends a JSON error with the key 'message'
    // as the error
    let message = await postResponse.text(); // Parse it as text
    let data = null;

    try {
      data = JSON.parse(message); // Try to parse it as json
      message = data.message;
      // eslint-disable-next-line no-empty
    } catch {}

    throw new Error(message);
  }

  // Re-fetch the Member so that the return will correctly update the apollo cache
  const response = await trelloFetch(
    networkClient.getUrl(
      `/1/member/${args.memberId}?fields=aaBlockSyncUntil,oneTimeMessagesDismissed,requiresAaOnboarding`,
    ),
    undefined,
    {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'unblockMemberProfileSync',
        operationType: 'query',
        operationName: context.operationName,
      },
    },
  );
  if (!response.ok) {
    throw new Error(
      `Invalid response status ${response.status} from GET ${response.url}`,
    );
  }
  const member = await response.json();

  return prepareDataForApolloCache(member, rootNode);
};

// Internal endpoint for development and trelloinc members
export const deleteOneTimeMessagesDismissed: TrelloRestResolver<MutationDeleteOneTimeMessagesDismissedArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const searchParams = new URLSearchParams();

  searchParams.set('value', args.message);
  searchParams.set('token', context.token || token || '');

  const networkClient = getNetworkClient();

  const response = await fetch(
    networkClient.getUrl(
      `/1/members/${args.memberId}/oneTimeMessagesDismissed`,
    ),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: searchParams,
    },
  );

  const member = await response.json();

  return prepareDataForApolloCache(member, rootNode);
};

export const addOneTimeMessagesDismissed: TrelloRestResolver<MutationAddOneTimeMessagesDismissedArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const searchParams = new URLSearchParams();

  searchParams.set('value', args.messageId);
  searchParams.set('token', context.token || token || '');

  const networkClient = getNetworkClient();

  const response = await fetch(
    networkClient.getUrl(
      `/1/members/${args.memberId}/oneTimeMessagesDismissed`,
    ),
    {
      method: 'POST',
      credentials: 'include',
      body: searchParams,
      headers: {
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
    },
  );

  const member = await response.json();

  return prepareDataForApolloCache(member, rootNode);
};

export const addMessageDismissed: TrelloRestResolver<MutationAddMessageDismissedArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/members/${args.memberId}/messagesDismissed`,
  );
  const response = await trelloFetch(
    apiUrl,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        name: args.name,
        token: context.token || token,
        lastDismissed: args.lastDismissed,
      }),
      headers: {
        'X-Trello-Client-Version': context.clientAwareness.version,
        'Content-Type': 'application/json',
      },
    },
    {
      clientVersion,
      networkRequestEventAttributes: {
        operationName: context.operationName,
        resolver: 'addMessageDismissed',
        operationType: 'mutation',
        source: 'graphql',
      },
    },
  );

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const member = await response.json();

  return prepareDataForApolloCache(member, rootNode);
};

export const memberAgreementsResolver: TrelloRestResolver<object> = async (
  member: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/members/${member.id}/agreements`);

  try {
    const response = await trelloFetch(apiUrl, undefined, {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Member.agreements',
        operationName: context.operationName,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model ? prepareDataForApolloCache(model, rootNode, 'Member') : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const memberAtlassianOrganizationsResolver: TrelloRestResolver<object> = async (
  member: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;
  let apiUrl;

  const linkToAtlassianOrgV2 = featureFlagClient.get(
    'enterprise.link-to-atlassian-org-v2',
    false,
  );

  if (linkToAtlassianOrgV2) {
    // Fetch candidate (vortex) orgs for linking
    apiUrl = `${atlassianApiGateway}gateway/api/adminhub/organization/vortex`;
  } else {
    const networkClient = getNetworkClient();
    apiUrl = networkClient.getUrl(
      `/1/members/${member.id}/atlassianOrganizations`,
    );
  }

  const response = await trelloFetch(apiUrl, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'Member.atlassianOrganizations',
      operationName: context.operationName,
    },
  });

  if (response.ok) {
    model = await response.json();
  } else if (response.status === 404) {
    return null;
  } else {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  // attempt to populate SSO URLs for any administered Orgs without a value
  await Promise.all(
    model.map(async (org: Member_Atlassian_Organization) => {
      if (linkToAtlassianOrgV2) {
        /**
          We only fetch orgs that we have determined
          the current user is an admin of
        */
        org.isIdentityAdmin = true;

        /**
          Server has been identifying an atl org as `linked` if its shell
          enterprise has paid products. This should not be possible, so `linked`
          is always false.
        */
        org.linked = false;

        /*
          When returning atlassian orgs, Server has been setting a prop on
          each org called `hasAtlassianAccess`. This field does not exist on
          the `Member_Atlassian_Organization` type, and is unused in web.
          So going forward, we won't be keeping parity by setting that field.
        */

        // Fetch claimed domains for each candidate org:
        org.domains = await atlassianOrganizationDomains(org.id, context);
      }

      /*
        Server always sends back an `ssoUrl` field.
        We make it the default value for `ssoUrls` here
        to minimize risk and for backwards compatibility.
      */
      org.ssoUrls = [org.ssoUrl];

      if (org.isIdentityAdmin) {
        try {
          org.ssoUrls = await atlassianOrganizationSsoUrl(
            member.id,
            org.id,
            context,
          );
        } catch (e) {
          // If the request to fetch the SAML configuration fails,
          // we don't want to re-throw that error from here, because there
          // could be successful requests for other AtlOrgs. But we do want
          // to surface that error in the UI. So we set flexAuthError and use
          // that in the component to show an error message.
          org.flexAuthError = true;
        }
      }
    }),
  );
  return model ? prepareDataForApolloCache(model, rootNode, 'Member') : model;
};

export const acceptDeveloperTerms: TrelloRestResolver<MutationAcceptDeveloperTermsArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/members/${args.memberId}/agreements`);

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        agreementType: 'developer-terms',
        token: context.token || token,
      }),
    });

    if (response.ok) {
      model = await response.json();
    } else {
      throw new Error(
        `An error occurred while resolving a GraphQL mutation. (status: ${response.status}, statusText: ${response.statusText})`,
      );
    }

    return model ? prepareDataForApolloCache(model, rootNode) : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const updateMemberProfile: TrelloRestResolver<MutationUpdateMemberProfileArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const operationNameInUrl = featureFlagClient.get(
    'fep.operation-name-in-url',
    false,
  );
  const queryString = operationNameInUrl
    ? `?operationName=${context.operationName}`
    : '';
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/members/me${queryString}`);
  const response = await fetch(apiUrl, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      ...args.profile,
      token: context.token || token || '',
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache(await response.json(), rootNode);
};

export const addCampaign: TrelloRestResolver<MutationAddCampaignArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl('/1/members/me/campaigns');
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      ...(args || {}),
      token: context.token || token,
    }),
  });

  if (response.ok) {
    return prepareDataForApolloCache(await response.json(), rootNode);
  }

  sendNetworkErrorEvent({
    url: apiUrl,
    response: await response.clone().text(),
    status: response.status,
    operationName: context.operationName,
  });
  throw await parseNetworkError(response);
};

export const updateCampaign: TrelloRestResolver<MutationUpdateCampaignArgs> = async (
  obj,
  args,
  context,
) => {
  const { campaignId, ...rest } = args;
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/members/me/campaigns/${campaignId}`);
  const response = await fetch(apiUrl, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      ...(rest || {}),
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }
};

export const changeMemberEmail: TrelloRestResolver<MutationChangeMemberEmailArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;
  let response = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl('/1/members/me/logins');

  const { email, loginId, primary } = args;

  try {
    response = await fetch(`${apiUrl}/${loginId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        email,
        primary,
        token: context.token || token,
      }),
    });

    if (response.ok) {
      model = await response.json();

      if (args.dismissMessage) {
        const networkClient = getNetworkClient();
        const dismissResponse = await fetch(
          networkClient.getUrl(`/1/member/me/oneTimeMessagesDismissed`),
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-Trello-Client-Version': context.clientAwareness.version,
            },
            body: JSON.stringify({
              token: context.token || token,
              value: args.dismissMessage,
            }),
          },
        );

        if (!dismissResponse.ok) {
          throw new Error(
            `Invalid response status ${dismissResponse.status} from POST ${dismissResponse.url}`,
          );
        }
      }
    } else {
      throw new Error(
        `An error occurred while resolving a GraphQL mutation. (status: ${response.status}, statusText: ${response.statusText})`,
      );
    }

    return model ? prepareDataForApolloCache(model, rootNode) : model;
  } catch (err) {
    if (response) {
      // Sometimes the server sends us just the string as the error, however
      // sometimes the server also sends a JSON error with the key 'message'
      // as the error
      let message = await response.text(); // Parse it as text
      let data = null;

      try {
        data = JSON.parse(message); // Try to parse it as json
        message = data.message;
        // eslint-disable-next-line no-empty
      } catch {}

      throw new Error(message);
    } else {
      throw new Error('Unknown error');
    }
  }
};

export const resendVerificationEmail: TrelloRestResolver<MutationResendVerificationEmailArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl('/resendValidate');

  const { email } = args;

  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      email,
      token: Cookie.get(`token${window.location.port ? '3000' : ''}`) || '',
    }),
  });

  if (response.ok) {
    model = await response.json();

    if (model.badEmail) {
      throw new Error('bad email');
    } else if (model.alreadyConfirmed) {
      throw new Error('already confirmed');
    }
  } else {
    throw new Error(
      `An error occurred while resolving a GraphQL mutation. (status: ${response.status}, statusText: ${response.statusText})`,
    );
  }

  return model ? prepareDataForApolloCache(model, rootNode) : model;
};

export const updateMemberActiveChannel: TrelloRestResolver<MutationUpdateMemberActiveChannelArgs> = async (
  parent,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const searchParams = new URLSearchParams();
  searchParams.set('value', args.channel);
  searchParams.set('token', context.token || token || '');

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/members/${args.memberId}/channels/active`,
  );

  const response = await fetch(apiUrl, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'X-Trello-Client-Version': context.clientAwareness.version,
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    },
    body: searchParams,
  });

  if (response.ok) {
    const model = await response.json();
    return prepareDataForApolloCache(
      { id: args.memberId, channels: model },
      rootNode,
    );
  } else {
    throw new Error(
      `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
    );
  }
};

export const memberAtlassianAccountsResolver: TrelloRestResolver<object> = async (
  parent,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl('/1/members/me/logins/atlassianAccounts');

  const response = await trelloFetch(apiUrl, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'atlassianAccounts',
      operationName: context.operationName,
    },
  });

  if (response.ok) {
    const model = await response.json();
    return prepareDataForApolloCache(model, rootNode);
  } else {
    throw new Error(
      `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
    );
  }
};

// Very specific resolver to use on /members/id/cards
export const memberCardsResolver: TrelloRestResolver<QueryMemberCardsArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { id, limit, before, modifiedSince, sort } = args;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/members/${id}/cards`);

  const params = new URLSearchParams();
  params.set('filter', 'visible');
  params.set('stickers', 'true');
  params.set('attachments', 'true');
  params.set('members', 'true');

  if (limit) {
    params.set('limit', limit.toString());
  }
  if (before) {
    params.set('before', before);
  }
  if (modifiedSince) {
    params.set('modifiedSince', modifiedSince.toString());
  }
  if (sort) {
    params.set('sort', sort);
  }

  const response = await trelloFetch(`${apiUrl}?${params}`, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'memberCards',
      operationName: context.operationName,
    },
  });

  if (response.ok) {
    const model = await response.json();
    return prepareDataForApolloCache(model, rootNode);
  } else {
    throw new Error(
      `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
    );
  }
};

export const memberSearchResolver: TrelloRestResolver<QueryMemberSearchArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/search/members`);

  const params = new URLSearchParams();

  Object.entries(args).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, value.toString());
    }
  });

  const response = await trelloFetch(`${apiUrl}?${params}`, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'memberSearch',
      operationName: context.operationName,
    },
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache(await response.json(), rootNode);
};

export const updateMarketingOptIn: TrelloRestResolver<MutationUpdateMarketingOptInArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { optedIn, prompt } = args;
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/members/me`);

  const response = await trelloFetch(
    apiUrl,
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        'marketingOptIn/optedIn': optedIn,
        'marketingOptIn/displayText': prompt,
        token: context.token || token,
      }),
    },
    {
      clientVersion,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'updateMarketingOptIn',
        operationName: context.operationName,
        operationType: 'mutation',
      },
    },
  );

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const member = await response.json();
  return prepareDataForApolloCache(member, rootNode);
};

export const updateNotificationEmailFrequency: TrelloRestResolver<MutationUpdateNotificationEmailFrequencyArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { id, frequency } = args;
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/members/${id}?prefs%2FminutesBetweenSummaries=${frequency}`,
  );

  const response = await trelloFetch(
    apiUrl,
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
      }),
    },
    {
      clientVersion,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'updateNotificationEmailFrequency',
        operationName: context.operationName,
        operationType: 'mutation',
      },
    },
  );

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const member = await response.json();
  return prepareDataForApolloCache(member, rootNode);
};
